/**
 * @fileoverview Componente Molecule para mostrar la tarjeta de información del cliente en el inbox.
 * Presenta datos clave del cliente, como nombre, teléfono, email y conteo de órdenes, con diseño optimizado y traducción internacionalizada.
 * @module CustomerCardMC
 * @category Component - Molecule
 * @since 1.0.0
 * @version 2.1.0
 */
import React, { memo } from "react";
import { useTranslation } from 'react-i18next';
import AvatarMC from '../../../components/AvatarMC';
import IconMC from "../../../components/IconMC";

/**
 * Componente Molecule para mostrar la tarjeta de información del cliente en el inbox.
 *
 * Atomic Design: Molecule
 * - Compuesto por AvatarMC (Atom) e IconMC (Atom)
 * - Presenta datos clave del cliente y su conteo de órdenes
 * - Utiliza TailwindCSS con .tw-wrapper
 * - Traducción internacionalizada con useTranslation
 */
const CustomerCardMC = memo(({
  customerData,
  orderCounts
}) => {
  // Hook de traducción internacionalizada
  const { t } = useTranslation('InboxMC');
  const {
    fullName,
    formattedPhone,
    email,
    hasEmail,
    hasPhone,
    username
  } = customerData;

  return (
    <div className="tw-wrapper tw-px-4 tw-pt-4">
      <div className="tw-flex tw-items-center">
        <AvatarMC
          image="person-fill"
          iconClassName="tw-text-blue-500"
          className="tw-mr-2"
          avatarClassName="tw-w-9 tw-h-9 tw-bg-blue-50"
        />
        <div className="tw-flex tw-flex-col tw-min-w-0">
          <div className="tw-font-semibold tw-text-sm tw-truncate tw-text-gray-950">
            {fullName}
          </div>
          {username && (
            <div className="tw-font-light tw-text-xs tw-truncate tw-text-gray-950">
              <span className="tw-text-gray-500">@</span>
              {username}
            </div>
          )}
        </div>
      </div>

      <div className="tw-gap-1 tw-mt-1">
        {hasPhone && (
          <div className="tw-flex tw-gap-1 tw-mb-1 tw-items-center tw-text-gray-700 tw-text-xs">
            <IconMC name="phone-outline" size="base" />
            {formattedPhone}
          </div>
        )}

        {hasEmail && (
          <div className="tw-flex tw-gap-1 tw-mb-1 tw-items-center tw-truncate tw-text-gray-700 tw-text-xs">
            <IconMC name="email-outline" size="base" />
            {email}
          </div>
        )}

        <div className="tw-flex tw-gap-1 tw-mb-1 tw-items-center tw-text-gray-700 tw-text-xs">
          <IconMC name="number-order" size="base" />
          {`${t('customer_card.orders')}: ${orderCounts || 0}`}
        </div>

      </div>
    </div>
  );
});

CustomerCardMC.displayName = 'CustomerCardMC';

export default CustomerCardMC;
